import React, { useState, useEffect, useCallback } from "react";
import Navbar from "../components/Navbar";
import { useConfirmDialog } from "../components/ConfirmDialog";
import { ToastProvider, useToast } from "../components/Toast";
import {
  getPortfolioCategory,
  createPortfolioProduct,
  updatePortfolioProduct,
  deletePortfolioProduct,
  uploadPortfolioImage,
  deletePortfolioImage,
  listPortfolioImages,
  getAnalyticsStats,
  getAnalyticsPageviews,
  getAnalyticsMetrics,
  getAdminUsers,
  forceLogoutSession,
  getPLCOverview,
  adminCreateClient,
  adminDeleteClient,
  adminDeletePLC,
  MEDIA_BASE_URL,
} from "../api";
import "./AdminPortfolio.css";

const CATEGORY_TEMPLATES = {
  heat_pump: { site: "", application: "", heatingCapacity: "", powerInput: "", extraText: "", installation: "" },
  process_chiller: { name: "", cooling_capacity: "", installation: "", application: "", compressor: "", plc: "" },
  ice_machine: { name: "", location: "", capacity: "", compressor: "", refrigerant: "", water_cooled_condenser: "", controller: "", remote: "" },
  co2_liquefaction: { name: "", location: "", capacityTPD: "", compressor: "", refrigerant: "", controller: "", numUnits: "" },
  co2_tank: { name: "", location: "", tankCapacity: "", refrigerant: "", refUnit: "", heatExchanger: "" },
};

// Categories where order is determined by a data field, not manual drag-and-drop
const AUTO_SORTED_CATEGORIES = new Set(["co2_liquefaction"]);

const CATEGORIES = [
  { key: "home_page", label: "Home Page" },
  { key: "heat_pump", label: "Heat Pump" },
  { key: "process_chiller", label: "Process Chiller" },
  { key: "ice_machine", label: "Ice Machine" },
  { key: "co2_liquefaction", label: "CO₂ Liquefaction" },
  { key: "co2_tank", label: "CO₂ Tank Storage" },
];

// ── Editable key-value spec editor ──────────────────────────────────────────

function SpecsEditor({ specs, onChange }) {
  const entries = Object.entries(specs);

  function updateKey(oldKey, newKey) {
    const next = {};
    for (const [k, v] of Object.entries(specs)) {
      next[k === oldKey ? newKey : k] = v;
    }
    onChange(next);
  }

  function updateValue(key, value) {
    onChange({ ...specs, [key]: value });
  }

  function removeEntry(key) {
    const next = { ...specs };
    delete next[key];
    onChange(next);
  }

  function addEntry() {
    let name = "newField";
    let i = 1;
    while (specs[name] !== undefined) name = `newField${i++}`;
    onChange({ ...specs, [name]: "" });
  }

  return (
    <div className="ap-specs-editor">
      {entries.map(([key, value]) => (
        <div key={key} className="ap-spec-row">
          <input
            className="ap-spec-key"
            value={key}
            onChange={(e) => updateKey(key, e.target.value)}
          />
          <span className="ap-spec-colon">:</span>
          <input
            className="ap-spec-value"
            value={typeof value === "object" ? JSON.stringify(value) : String(value ?? "")}
            onChange={(e) => updateValue(key, e.target.value)}
          />
          <button
            className="ap-btn ap-btn-danger ap-btn-sm"
            onClick={() => removeEntry(key)}
            title="Remove field"
          >
            ×
          </button>
        </div>
      ))}
      <button className="ap-btn ap-btn-secondary ap-btn-sm" onClick={addEntry}>
        + Add field
      </button>
    </div>
  );
}

// ── Image paths editor ───────────────────────────────────────────────────────

function ImagePathsEditor({ imagePaths, category, onChange }) {
  const toast = useToast();
  const [uploading, setUploading] = useState(false);
  const [browserImages, setBrowserImages] = useState([]);
  const [showBrowser, setShowBrowser] = useState(false);

  async function handleUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadPortfolioImage(category, file);
      onChange([...imagePaths, result.path]);
    } catch (err) {
      toast(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  async function openBrowser() {
    try {
      const result = await listPortfolioImages(category);
      setBrowserImages(result.images || []);
      setShowBrowser(true);
    } catch (err) {
      toast(`Could not list images: ${err.message}`);
    }
  }

  function addFromBrowser(path) {
    if (!imagePaths.includes(path)) onChange([...imagePaths, path]);
  }

  function removePath(index) {
    onChange(imagePaths.filter((_, i) => i !== index));
  }

  function moveUp(index) {
    if (index === 0) return;
    const next = [...imagePaths];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    onChange(next);
  }

  return (
    <div className="ap-images-editor">
      {imagePaths.map((path, i) => (
        <div key={i} className="ap-image-row">
          <img src={`${MEDIA_BASE_URL}${path}`} alt="" className="ap-thumb" />
          <span className="ap-image-path">{path}</span>
          <div className="ap-image-actions">
            {i > 0 && (
              <button className="ap-btn ap-btn-secondary ap-btn-sm" onClick={() => moveUp(i)}>↑</button>
            )}
            <button className="ap-btn ap-btn-danger ap-btn-sm" onClick={() => removePath(i)}>×</button>
          </div>
        </div>
      ))}

      <div className="ap-image-add-row">
        <label className="ap-btn ap-btn-secondary ap-btn-sm ap-upload-label">
          {uploading ? "Uploading…" : "Upload image"}
          <input type="file" accept="image/*" onChange={handleUpload} disabled={uploading} style={{ display: "none" }} />
        </label>
        <button className="ap-btn ap-btn-secondary ap-btn-sm" onClick={openBrowser}>
          Browse existing
        </button>
      </div>

      {showBrowser && (
        <div className="ap-image-browser">
          <div className="ap-image-browser-header">
            <span>Images in {category}/</span>
            <button className="ap-btn ap-btn-secondary ap-btn-sm" onClick={() => setShowBrowser(false)}>Close</button>
          </div>
          <div className="ap-image-browser-grid">
            {browserImages.length === 0 && <span className="ap-muted">No images found.</span>}
            {browserImages.map((path) => (
              <div key={path} className="ap-browser-item" onClick={() => addFromBrowser(path)} title={path}>
                <img src={`${MEDIA_BASE_URL}${path}`} alt="" className="ap-browser-thumb" />
                <span className="ap-browser-name">{path.split("/").pop()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Edit / Create modal ──────────────────────────────────────────────────────

function ProductModal({ product, category, onSave, onClose, defaultOrder = 0 }) {
  const toast = useToast();
  const isNew = !product;
  const [specs, setSpecs] = useState(product?.specs ?? CATEGORY_TEMPLATES[category] ?? {});
  const [imagePaths, setImagePaths] = useState(product?.image_paths ?? []);
  const [displayOrder, setDisplayOrder] = useState(product?.display_order ?? defaultOrder);
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      if (isNew) {
        await createPortfolioProduct(category, { specs, image_paths: imagePaths, display_order: displayOrder });
      } else {
        await updatePortfolioProduct(product.id, { specs, image_paths: imagePaths, display_order: displayOrder });
      }
      onSave();
    } catch (err) {
      toast(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="ap-modal-backdrop" onClick={onClose}>
      <div className="ap-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ap-modal-header">
          <h2>{isNew ? "Add product" : "Edit product"}</h2>
          <button className="ap-btn ap-btn-secondary ap-btn-sm" onClick={onClose}>✕</button>
        </div>

        {!AUTO_SORTED_CATEGORIES.has(category) && (
          <div className="ap-modal-section">
            <label className="ap-label">Display order</label>
            <input
              type="number"
              className="ap-input"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(Number(e.target.value))}
            />
          </div>
        )}

        <div className="ap-modal-section">
          <label className="ap-label">Specs</label>
          <SpecsEditor specs={specs} onChange={setSpecs} />
        </div>

        <div className="ap-modal-section">
          <label className="ap-label">Images</label>
          <ImagePathsEditor imagePaths={imagePaths} category={category} onChange={setImagePaths} />
        </div>

        <div className="ap-modal-footer">
          <button className="ap-btn ap-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="ap-btn ap-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Product card ─────────────────────────────────────────────────────────────

function ProductCard({ product, onEdit, onDelete, draggable, onDragStart, onDragOver, onDrop, onDragEnd, isDragOver }) {
  const firstImage = product.image_paths?.[0];
  const specEntries = Object.entries(product.specs ?? {}).slice(0, 4);

  return (
    <div
      className={`ap-card${isDragOver ? " ap-card-drag-over" : ""}${!draggable ? " ap-card-no-drag" : ""}`}
      draggable={draggable}
      onDragStart={draggable ? onDragStart : undefined}
      onDragOver={draggable ? (e) => { e.preventDefault(); onDragOver(); } : undefined}
      onDrop={draggable ? (e) => { e.preventDefault(); onDrop(); } : undefined}
      onDragEnd={draggable ? onDragEnd : undefined}
    >
      {draggable && <div className="ap-drag-handle" title="Drag to reorder">⠿</div>}
      {firstImage && (
        <img src={`${MEDIA_BASE_URL}${firstImage}`} alt="" className="ap-card-img" />
      )}
      <div className="ap-card-body">
        <div className="ap-card-specs">
          {specEntries.map(([k, v]) => (
            <div key={k} className="ap-card-spec-row">
              <span className="ap-card-spec-key">{k}:</span>
              <span className="ap-card-spec-val">{String(v ?? "")}</span>
            </div>
          ))}
          {Object.keys(product.specs ?? {}).length > 4 && (
            <span className="ap-muted">+{Object.keys(product.specs).length - 4} more</span>
          )}
        </div>
        <div className="ap-card-footer">
          <div>
            <button className="ap-btn ap-btn-secondary ap-btn-sm" onClick={onEdit}>Edit</button>
            <button className="ap-btn ap-btn-danger ap-btn-sm" onClick={onDelete}>Delete</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Role helper ───────────────────────────────────────────────────────────────

function getRole() {
  try {
    const token = localStorage.getItem("token");
    if (!token) return null;
    if (token === "mock-token") return localStorage.getItem("mockRole");
    return JSON.parse(atob(token.split(".")[1])).role || null;
  } catch { return null; }
}

// ── Analytics dashboard ───────────────────────────────────────────────────────

// ── PLC Admin ─────────────────────────────────────────────────────────────────

const EMPTY_CLIENT = { username: "", password: "" };

function ClientModal({ onSave, onClose }) {
  const [form, setForm] = useState(EMPTY_CLIENT);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setSaving(true); setErr(null);
    try {
      await adminCreateClient(form.username, form.password);
      onSave();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="ap-modal-backdrop" onClick={onClose}>
      <div className="ap-modal" onClick={e => e.stopPropagation()}>
        <div className="ap-modal-header">
          <h2>Add Client</h2>
          <button className="ap-modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={submit}>
          <div className="ap-modal-section">
            <label className="ap-label">Username *</label>
            <input className="ap-input" value={form.username} onChange={e => set("username", e.target.value)} required />
          </div>
          <div className="ap-modal-section">
            <label className="ap-label">Password *</label>
            <input className="ap-input" type="password" value={form.password} onChange={e => set("password", e.target.value)} required />
          </div>
          {err && <div style={{padding: "0 24px 8px"}}><div className="ap-error">{err}</div></div>}
          <div className="ap-modal-footer">
            <button type="button" className="ap-btn ap-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="ap-btn ap-btn-primary" disabled={saving}>{saving ? "Creating…" : "Create"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function PLCAdmin() {
  const toast = useToast();
  const [askConfirm, confirmDialog] = useConfirmDialog(toast);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientModal, setClientModal] = useState(false);
  const [expanded, setExpanded] = useState({});     // plcId → bool

  async function load() {
    setLoading(true); setError(null);
    try { setClients(await getPLCOverview()); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  // The PLC editor opens in a separate tab; refresh the list when we return so
  // edits made there show up without a manual reload.
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  function togglePLC(id) { setExpanded(e => ({ ...e, [id]: !e[id] })); }

  function handleDeleteClient(client) {
    askConfirm({
      title: "Delete client?",
      message: `This permanently deletes "${client.username}" and all their PLCs.`,
      confirmLabel: "Delete client",
      action: async () => { await adminDeleteClient(client.id); load(); },
    });
  }

  function handleDeletePLC(plc) {
    askConfirm({
      title: "Delete PLC?",
      message: `This permanently deletes "${plc.name}" and its configuration.`,
      confirmLabel: "Delete PLC",
      action: async () => { await adminDeletePLC(plc.id); load(); },
    });
  }

  return (
    <div className="plca-root">
      <div className="plca-toolbar">
        <button className="ap-btn ap-btn-primary" onClick={() => setClientModal(true)}>+ Add Client</button>
        <a className="ap-btn ap-btn-secondary ap-btn-link" href="/admin/plc/new" target="_blank" rel="noopener noreferrer">+ Add PLC</a>
      </div>

      {loading && <div className="ap-loading">Loading…</div>}
      {error && <div className="ap-error">{error}</div>}

      {!loading && !error && clients.length === 0 && (
        <div className="ap-empty">No clients yet. Add one to get started.</div>
      )}

      {!loading && !error && clients.map(client => (
        <div key={client.id} className="plca-client-card">
          <div className="plca-client-header">
            <div className="plca-client-info">
              <span className="plca-client-name">{client.username}</span>
              <span className="plca-client-meta">{client.plcs.length} PLC{client.plcs.length !== 1 ? "s" : ""}</span>
            </div>
            <div className="plca-client-actions">
              <a
                className="ap-btn ap-btn-secondary ap-btn-sm ap-btn-link"
                href={`/admin/plc/new?owner=${client.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                + Add PLC
              </a>
              <button
                className="ap-btn ap-btn-danger ap-btn-sm"
                onClick={() => handleDeleteClient(client)}
              >
                Delete client
              </button>
            </div>
          </div>

          {client.plcs.length === 0 && (
            <div className="plca-empty-plcs">No PLCs assigned</div>
          )}

          {client.plcs.map(plc => (
            <div key={plc.id} className="plca-plc-row">
              <div className="plca-plc-main">
                <button className="plca-expand-btn" onClick={() => togglePLC(plc.id)}>
                  {expanded[plc.id] ? "▾" : "▸"}
                </button>
                <div className="plca-plc-info">
                  <span className="plca-plc-name">{plc.name}</span>
                  {plc.location && <span className="plca-plc-meta">{plc.location}</span>}
                  <span className="plca-plc-url">{plc.url}</span>
                </div>
                <div className="plca-plc-actions">
                  <a className="ap-btn ap-btn-secondary ap-btn-sm ap-btn-link" href={`/admin/plc/${plc.id}/edit`} target="_blank" rel="noopener noreferrer">Edit</a>
                  <button className="ap-btn ap-btn-danger ap-btn-sm" onClick={() => handleDeletePLC(plc)}>Delete</button>
                </div>
              </div>

              {expanded[plc.id] && (
                <div className="plca-ivars">
                  <span className="plca-ivars-label">Important variables:</span>
                  {plc.important_vars.length === 0
                    ? <span className="plca-ivars-empty">None set</span>
                    : plc.important_vars.map(v => {
                        const name = typeof v === "string" ? v : v.variable_name;
                        const label = (typeof v === "object" && v.display_name) ? v.display_name : name;
                        return <span key={name} className="plca-ivar-chip" title={name}>{label}</span>;
                      })
                  }
                </div>
              )}
            </div>
          ))}
        </div>
      ))}

      {clientModal && (
        <ClientModal
          onSave={() => { setClientModal(false); load(); }}
          onClose={() => setClientModal(false)}
        />
      )}

      {confirmDialog}
    </div>
  );
}

// ── Admin sections (sidebar nav) ──────────────────────────────────────────────

const ADMIN_SECTIONS = [
  {
    key: "products",
    label: "Products",
    sub: "Portfolio content shown on the public site, by category.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
      </svg>
    ),
  },
  {
    key: "plcs",
    label: "Clients & PLCs",
    sub: "Client accounts and the PLCs assigned to them.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 002.25-2.25V6.75a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 6.75v10.5a2.25 2.25 0 002.25 2.25zm.75-12h9v9h-9v-9z" />
      </svg>
    ),
  },
  {
    key: "users",
    label: "Users",
    sub: "Active sessions across devices — force logout if something looks off.",
    superOnly: true,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
      </svg>
    ),
  },
  {
    key: "analytics",
    label: "Analytics",
    sub: "Website traffic, devices and visitor stats.",
    superOnly: true,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
      </svg>
    ),
  },
];

// ── Users view ────────────────────────────────────────────────────────────────

function daysAgo(isoString) {
  if (!isoString) return "—";
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return "—";
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days < 0) return "just now";   // clock skew / sub-day diff
  if (days === 0) return "today";
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
}

const ROLE_ORDER = { superadmin: 0, admin: 1, client: 2 };

function UsersView() {
  const toast = useToast();
  const [askConfirm, confirmDialog] = useConfirmDialog(toast);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loggingOut, setLoggingOut] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminUsers();
      data.sort((a, b) => (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9));
      setUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function handleLogout(sessionId, username) {
    askConfirm({
      title: "Log out session?",
      message: `This will end the active session for ${username}.`,
      confirmLabel: "Log out",
      action: async () => {
        setLoggingOut(sessionId);
        try {
          await forceLogoutSession(sessionId);
          await load();
        } finally {
          setLoggingOut(null);
        }
      },
    });
  }

  const ROLE_COLORS = { superadmin: "uv-role-super", admin: "uv-role-admin", client: "uv-role-client" };

  return (
    <div className="uv-root">
      {loading && <div className="uv-loading">Loading users…</div>}
      {error && <div className="uv-error">{error}</div>}
      {!loading && !error && (
        <div className="uv-list">
          {users.map((user) => (
            <div key={user.id} className="uv-user-card">
              <div className="uv-user-header">
                <span className="uv-username">{user.username}</span>
                <span className={`uv-role-badge ${ROLE_COLORS[user.role] || ""}`}>{user.role}</span>
                <span className="uv-session-count">
                  {user.sessions.length === 0
                    ? "No active sessions"
                    : `${user.sessions.length} session${user.sessions.length > 1 ? "s" : ""}`}
                </span>
              </div>
              {user.sessions.length > 0 && (
                <div className="uv-sessions">
                  <div className="uv-sessions-head">
                    <span>Device</span>
                    <span>Logged in</span>
                    <span>Last active</span>
                    <span></span>
                  </div>
                  {user.sessions.map((s) => (
                    <div key={s.id} className="uv-session-row">
                      <span className="uv-device">{s.device_hint || "Unknown"}</span>
                      <span className="uv-time">{daysAgo(s.issued_at)}</span>
                      <span className="uv-time">{daysAgo(s.last_used_at)}</span>
                      <button
                        className="uv-logout-btn"
                        disabled={loggingOut === s.id}
                        onClick={() => handleLogout(s.id, user.username)}
                      >
                        {loggingOut === s.id ? "…" : "Log out"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {confirmDialog}
    </div>
  );
}

const RANGES = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
];

function StatCard({ label, value, sub }) {
  return (
    <div className="an-stat-card">
      <div className="an-stat-value">{value ?? "—"}</div>
      <div className="an-stat-label">{label}</div>
      {sub && <div className="an-stat-sub">{sub}</div>}
    </div>
  );
}

function MetricTable({ title, rows, valueLabel = "Views" }) {
  return (
    <div className="an-table-card">
      <div className="an-table-title">{title}</div>
      {rows.length === 0 ? (
        <div className="an-table-empty">No data</div>
      ) : (
        <table className="an-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>{valueLabel}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="an-table-name" title={r.x}>{r.x}</td>
                <td className="an-table-val">{r.y}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AnalyticsDashboard() {
  const [range, setRange] = useState(30);
  const [stats, setStats] = useState(null);
  const [pages, setPages] = useState([]);
  const [devices, setDevices] = useState([]);
  const [countries, setCountries] = useState([]);
  const [referrers, setReferrers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      const endAt = Date.now();
      const startAt = endAt - range * 24 * 60 * 60 * 1000;
      try {
        const [s, pg, dev, co, ref] = await Promise.all([
          getAnalyticsStats(startAt, endAt),
          getAnalyticsMetrics(startAt, endAt, "url", 10),
          getAnalyticsMetrics(startAt, endAt, "device", 10),
          getAnalyticsMetrics(startAt, endAt, "country", 10),
          getAnalyticsMetrics(startAt, endAt, "referrer", 10),
        ]);
        const toArr = (x) => Array.isArray(x) ? x : (x?.data ?? []);
        // Normalize stats: handle both { visitors: {value} } and { visitors: N }
        const rawStats = s?.data ?? s;
        setStats(rawStats);
        setPages(toArr(pg));
        setDevices(toArr(dev));
        setCountries(toArr(co));
        setReferrers(toArr(ref));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [range]);

  // Extract stat value — handles both {value: N} and plain N; tries fallback keys
  function sv(stats, ...keys) {
    for (const k of keys) {
      const f = stats?.[k];
      if (f != null) return typeof f === "object" ? f.value : f;
    }
    return null;
  }

  function fmt(n) {
    if (n == null) return "—";
    return Number(n).toLocaleString();
  }

  function pct(n) {
    if (n == null) return "—";
    return `${(Number(n) * 100).toFixed(1)}%`;
  }

  function dur(secs) {
    if (secs == null) return "—";
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  }

  return (
    <div className="an-root">
      <div className="an-toolbar">
        <div className="an-range-btns">
          {RANGES.map((r) => (
            <button
              key={r.days}
              className={`ap-tab${range === r.days ? " ap-tab-active" : ""}`}
              onClick={() => setRange(r.days)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="ap-loading">Loading…</div>}
      {error && <div className="ap-error">Could not load analytics: {error}</div>}

      {!loading && !error && stats && (
        <>
          <div className="an-stat-grid">
            <StatCard label="Unique visitors" value={fmt(sv(stats, "visitors", "uniques"))} />
            <StatCard label="Page views" value={fmt(sv(stats, "pageviews"))} />
            <StatCard label="Visits" value={fmt(sv(stats, "visits"))} />
            <StatCard label="Bounce rate" value={pct(sv(stats, "bounces") / (sv(stats, "visits") || 1))} />
            <StatCard label="Avg visit duration" value={dur(sv(stats, "totaltime") / (sv(stats, "visits") || 1))} />
          </div>

          <div className="an-tables-grid">
            <MetricTable title="Top pages" rows={pages} valueLabel="Views" />
            <MetricTable title="Referrers" rows={referrers} valueLabel="Visits" />
            <MetricTable title="Devices" rows={devices} valueLabel="Visitors" />
            <MetricTable title="Countries" rows={countries} valueLabel="Visitors" />
          </div>
        </>
      )}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

function AdminPortfolio() {
  const toast = useToast();
  const [askConfirm, confirmDialog] = useConfirmDialog(toast);
  const isSuperAdmin = getRole() === "superadmin";
  const [section, setSection] = useState("products"); // "products" | "plcs" | "users" | "analytics"
  const [activeCategory, setActiveCategory] = useState(CATEGORIES[0].key);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingProduct, setEditingProduct] = useState(undefined); // undefined = closed, null = new, object = editing
  const [savingOrder, setSavingOrder] = useState(false);
  const dragIndex = React.useRef(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const HOME_PAGE_DEFAULTS = [
    { redirect: "co2liquefaction",  name: "CO₂ and N₂O Liquefaction",            caption: "For CO₂ Recovery & Generation Plants",    image: "/machines-images/CO2 Liquefaction/kanpur_3600kg.jpg" },
    { redirect: "co2tankstorage",   name: "CO₂ and N₂O Storage",                 caption: "Maintain CO₂ Pressure in Tanks",           image: "/machines-images/CO2 Tank/Abu_70klx3.jpg" },
    { redirect: "heatpumps",        name: "Heat Pumps",                           caption: "For Pools and Bathrooms",                  image: "/machines-images/Heat Pump/Machine/Dehradun_Heat_Pump.jpeg" },
    { redirect: "mushroomfarms",    name: "Climate Control for Mushroom Farms",   caption: "",                                         image: "/machines-images/Mushroom Farms/mushroom.jpeg" },
    { redirect: "slaughterhouse",   name: "Refrigeration for Slaughterhouse",     caption: "",                                         image: "/machines-images/Slaughterhouse/slaughterhouse.jpeg" },
    { redirect: "icemachines",      name: "Ice Machines",                         caption: "Tube & Flake",                             image: "/machines-images/Tube Ice Machine/cbpl_52.jpg" },
    { redirect: "processchillers",  name: "Process Chillers",                     caption: "Cooling Oil, Milk, Exo-Gas",               image: "/machines-images/Process Chiller/milkchiller80hp.jpg" },
    { redirect: "coldstores",       name: "Refrigeration for Potato Storage",     caption: "For Fruits, Vegetables and Flowers",       image: "/machines-images/Cold Stores/mushroom1.jpeg" },
    { redirect: "wasteheatrecovery",name: "Waste Heat Recovery",                  caption: "Hot Water for Pools & Bathrooms",          image: "/machines-images/Waste Heat Recovery/reethi_beach.jpeg" },
  ];

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let data = await getPortfolioCategory(activeCategory);
      // Auto-seed home_page on first visit if empty
      if (activeCategory === "home_page" && data.length === 0) {
        await Promise.all(
          HOME_PAGE_DEFAULTS.map((m, i) =>
            createPortfolioProduct("home_page", {
              specs: { redirect: m.redirect, name: m.name, caption: m.caption },
              image_paths: [m.image],
              display_order: i,
            })
          )
        );
        data = await getPortfolioCategory("home_page");
      }
      setProducts(data.sort((a, b) => a.display_order - b.display_order));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [activeCategory]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  function handleDelete(product) {
    const name = product.specs?.name || product.specs?.site || `product #${product.id}`;
    askConfirm({
      title: "Delete product?",
      message: `This permanently deletes "${name}" from the portfolio.`,
      confirmLabel: "Delete product",
      action: async () => { await deletePortfolioProduct(product.id); loadProducts(); },
    });
  }

  async function handleDrop(dropIndex) {
    const from = dragIndex.current;
    dragIndex.current = null;
    setDragOverIndex(null);
    if (from === null || from === dropIndex) return;
    const reordered = [...products];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(dropIndex, 0, moved);
    const updated = reordered.map((p, i) => ({ ...p, display_order: i }));
    setProducts(updated);
    setSavingOrder(true);
    try {
      await Promise.all(
        updated.map((p) => updatePortfolioProduct(p.id, { display_order: p.display_order }))
      );
    } catch (err) {
      toast(`Failed to save order: ${err.message}`);
      loadProducts();
    } finally {
      setSavingOrder(false);
    }
  }

  function handleDragEnd() {
    dragIndex.current = null;
    setDragOverIndex(null);
  }

  const visibleSections = ADMIN_SECTIONS.filter(s => !s.superOnly || isSuperAdmin);
  const currentSection = ADMIN_SECTIONS.find(s => s.key === section) || ADMIN_SECTIONS[0];

  return (
    <div className="ap-page">
      <Navbar />
      <div className="ap-shell">
        <aside className="ap-sidebar">
          <div className="ap-sidebar-head">
            <span className="ap-sidebar-title">Admin</span>
            <span className="ap-sidebar-role">{isSuperAdmin ? "superadmin" : "admin"}</span>
          </div>
          <nav className="ap-sidebar-nav">
            {visibleSections.map((s) => (
              <button
                key={s.key}
                className={`ap-nav-item${section === s.key ? " ap-nav-item-active" : ""}`}
                onClick={() => setSection(s.key)}
              >
                <span className="ap-nav-icon">{s.icon}</span>
                <span className="ap-nav-label">{s.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        <main className="ap-main">
          <header className="ap-page-head">
            <h1 className="ap-title">{currentSection.label}</h1>
            <p className="ap-page-sub">{currentSection.sub}</p>
          </header>

        {section === "plcs" && <PLCAdmin />}
        {section === "users" && <UsersView />}
        {section === "analytics" && <AnalyticsDashboard />}

        {section === "products" && <>
        <div className="ap-tabs">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.key}
              className={`ap-tab${activeCategory === cat.key ? " ap-tab-active" : ""}`}
              onClick={() => setActiveCategory(cat.key)}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <div className="ap-toolbar">
          <span className="ap-category-title">
            {CATEGORIES.find((c) => c.key === activeCategory)?.label}
            {!loading && <span className="ap-muted"> — {products.length} products</span>}
            {savingOrder && <span className="ap-muted"> — saving order…</span>}
          </span>
          <button className="ap-btn ap-btn-primary" onClick={() => setEditingProduct(null)}>
            + Add product
          </button>
        </div>

        {activeCategory === "co2_liquefaction" && (
          <div className="ap-info-banner">
            Drag-and-drop reordering is disabled for this category — products are automatically sorted by descending capacity on the public page.
            When entering capacity, use only the number (e.g. <strong>100</strong>, not "100 TPD").
          </div>
        )}

        {loading && <div className="ap-loading">Loading…</div>}
        {error && <div className="ap-error">Error: {error}</div>}

        {!loading && !error && (
          <div className="ap-grid" onDragLeave={() => setDragOverIndex(null)}>
            {products.length === 0 && (
              <div className="ap-empty">No products yet. Add one to get started.</div>
            )}
            {products.map((product, index) => {
              const draggable = !AUTO_SORTED_CATEGORIES.has(activeCategory);
              return (
                <ProductCard
                  key={product.id}
                  product={product}
                  onEdit={() => setEditingProduct(product)}
                  onDelete={() => handleDelete(product)}
                  draggable={draggable}
                  onDragStart={draggable ? () => { dragIndex.current = index; } : undefined}
                  onDragOver={draggable ? () => setDragOverIndex(index) : undefined}
                  onDrop={draggable ? () => handleDrop(index) : undefined}
                  onDragEnd={draggable ? handleDragEnd : undefined}
                  isDragOver={draggable && dragOverIndex === index && dragIndex.current !== index}
                />
              );
            })}
          </div>
        )}

        </>}
        </main>
      </div>

      {editingProduct !== undefined && (
        <ProductModal
          product={editingProduct}
          category={activeCategory}
          defaultOrder={products.length > 0 ? Math.max(...products.map(p => p.display_order)) + 1 : 0}
          onSave={() => { setEditingProduct(undefined); loadProducts(); }}
          onClose={() => setEditingProduct(undefined)}
        />
      )}

      {confirmDialog}
    </div>
  );
}

export default function AdminPortfolioPage() {
  return (
    <ToastProvider>
      <AdminPortfolio />
    </ToastProvider>
  );
}
